import { observer } from 'mobx-react-lite'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIconDecoration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { EditorPage } from '@/features/legends/components/EditorPage'
import {
  Inspector,
  InspectorContent,
  InspectorHeader,
  InspectorSection,
} from '@/features/legends/components/Inspector'
import { ErrorTabItem } from '@/features/legends/components/TabLabel'
import type { InfoLineDesignee } from '@/features/legends/options/legend-options'
import { FiltersBuilderContainer } from '@/features/legends/filters/components/FiltersBuilder/FiltersBuilderContainer'
import {
  InfoLineRow,
  InfoLines,
} from '@/features/legends/options/components/InfoLines'
import {
  designees,
  type InfoLineEditorViewModel,
  type InfoLineTab,
} from '@/features/legends/options/InfoLineEditorViewModel'
import type { InfoLinesBuilderViewModel } from '@/features/legends/options/InfoLinesBuilderViewModel'
import type { InfoOptionsViewModel } from '@/features/legends/options/InfoOptionsViewModel'
import { FormulaFieldContainer } from '@/features/legends/ryno/components/FormulaFieldContainer'

export interface InfoOptionsContainerProps {
  vm: InfoOptionsViewModel
}

const testId = 'legend-info-lines'

const designeeItems: [InfoLineDesignee][] = designees.map((d) => [d])

const inspectorTabs: { tab: InfoLineTab; title: string }[] = [
  { tab: 'General', title: 'General' },
  { tab: 'Conditions', title: 'Conditions' },
]

/**
 * The legend's info lines, with an inspector for the selected line.
 */
export const InfoOptionsContainer = observer(function InfoOptionsContainer({
  vm,
}: InfoOptionsContainerProps) {
  const linesVm = vm.infoLines
  return (
    <>
      <EditorPage testId={`${testId}-page`}>
        <InfoLines
          onAdd={linesVm.add}
          isDisabled={linesVm.isEditingDisabled}
          testId={testId}
        >
          {linesVm.lines.map((lineVm, index) => (
            <InfoLineRow
              key={lineVm.cid}
              index={index}
              onMove={linesVm.moveByIndex}
              title={lineVm.title}
              summary={lineVm.lastContentLine}
              designee={lineVm.designee}
              isSelected={linesVm.selectedLineVm === lineVm}
              hasError={!lineVm.validation.isValid}
              isDisabled={linesVm.isEditingDisabled}
              onPress={() => linesVm.edit(lineVm)}
              onRemove={() => linesVm.remove(lineVm)}
              testId={`${testId}-line-${lineVm.cid}`}
            />
          ))}
        </InfoLines>
      </EditorPage>
      <InfoLineInspectorContainer vm={linesVm} />
    </>
  )
})

interface InfoLineInspectorContainerProps {
  vm: InfoLinesBuilderViewModel
}

const InfoLineInspectorContainer = observer(
  function InfoLineInspectorContainer({ vm }: InfoLineInspectorContainerProps) {
    const lineVm = vm.selectedLineVm
    if (!lineVm) {
      return (
        <Inspector testId={`${testId}-inspector`}>
          <EmptyState size="sm" grow>
            <EmptyStateIconDecoration icon="info" />
            <EmptyStateTitle>No line selected</EmptyStateTitle>
            <EmptyStateDescription>
              Select an info line to edit its content and conditions.
            </EmptyStateDescription>
          </EmptyState>
        </Inspector>
      )
    }
    return (
      <Inspector testId={`${testId}-inspector`}>
        <InspectorHeader
          tabs={inspectorTabs.map(({ tab, title }) => (
            <ErrorTabItem
              key={tab}
              title={title}
              error={lineVm.getTabError(tab)}
              isActive={vm.inspectorTab === tab}
              badgeCount={
                tab === 'Conditions' ? lineVm.triggerWhen.length : undefined
              }
              onPress={() => vm.setInspectorTab(tab)}
              testId={`${testId}-inspector-${tab.toLowerCase()}`}
            />
          ))}
        />
        <InspectorContent>
          <InspectorSection>
            {vm.inspectorTab === 'General' ? (
              <InfoLineGeneralContainer vm={lineVm} />
            ) : (
              <FiltersBuilderContainer
                vm={lineVm.triggerWhen}
                emptyText="No conditions configured; this line is always visible."
                testId={`${testId}-conditions`}
              />
            )}
          </InspectorSection>
        </InspectorContent>
      </Inspector>
    )
  },
)

interface InfoLineGeneralContainerProps {
  vm: InfoLineEditorViewModel
}

const InfoLineGeneralContainer = observer(function InfoLineGeneralContainer({
  vm,
}: InfoLineGeneralContainerProps) {
  return (
    <>
      <TextInputContainer
        model={vm}
        field="title"
        label="Title"
        help="What does the content represent?"
        error={vm.validation.getError('title')}
        isDisabled={vm.isEditingDisabled}
        data-test={`${testId}-title`}
      />
      <SelectContainer
        model={vm}
        field="designee"
        items={designeeItems}
        label="Designee"
        help="Who is this information intended for?"
        error={vm.validation.getError('designee')}
        isDisabled={vm.isEditingDisabled}
        data-test={`${testId}-designee`}
      />
      <FormulaFieldContainer
        label="Content formula"
        help="Ryno formula for the content."
        vm={vm.content}
        error={vm.validation.getError('content')}
        isDisabled={vm.isEditingDisabled}
        testId={`${testId}-content`}
      />
    </>
  )
})
